<script setup>
import { ref, onMounted, watch } from 'vue';

const showDialog = ref(false);
const theme = ref(localStorage.getItem('theme') || 'system');

const applyTheme = (mode) => {
  const root = document.documentElement;
  
  // 기존 클래스 제거
  root.classList.remove('light-mode', 'dark-mode');
  
  if (mode === 'light') {
    root.classList.add('light-mode');
  } else if (mode === 'dark') {
    root.classList.add('dark-mode');
  }
  // mode === 'system' 이면 클래스 없음 (시스템 따라감)
};

const setTheme = (mode) => {
  theme.value = mode;
  localStorage.setItem('theme', mode);
  applyTheme(mode);
  showDialog.value = false;
};

// 초기 적용
onMounted(() => {
  applyTheme(theme.value);
});

// 테마 변경 감지
watch(theme, (newTheme) => {
  applyTheme(newTheme);
});
</script>

<template>
  <!-- 트리거 버튼 -->
  <button class="theme-trigger" @click="showDialog = true">
    <span v-if="theme === 'system'">🖥️</span>
    <span v-else-if="theme === 'light'">☀️</span>
    <span v-else>🌙</span>
  </button>

  <!-- 다이얼로그 -->
  <Teleport to="body">
    <Transition name="dialog">
      <div v-if="showDialog" class="dialog-overlay" @click="showDialog = false">
        <div class="dialog-content" @click.stop>
          <div class="dialog-header">
            <h3>테마 설정</h3>
            <button class="close-btn" @click="showDialog = false">✕</button>
          </div>
          
          <div class="theme-options">
            <button 
              class="theme-option" 
              :class="{ active: theme === 'system' }"
              @click="setTheme('system')"
            >
              <span class="icon">🖥️</span>
              <span class="label">시스템</span>
            </button>
            
            <button 
              class="theme-option" 
              :class="{ active: theme === 'light' }"
              @click="setTheme('light')"
            >
              <span class="icon">☀️</span>
              <span class="label">라이트</span>
            </button>
            
            <button 
              class="theme-option" 
              :class="{ active: theme === 'dark' }"
              @click="setTheme('dark')"
            >
              <span class="icon">🌙</span>
              <span class="label">다크</span>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.theme-trigger {
  padding: 8px 12px;
  background: var(--color-background);
  border: 1px solid var(--color-text-primary);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: var(--font-size-lg);
  transition: all 0.2s;
}

.theme-trigger:hover {
  background: var(--color-background-light);
  transform: scale(1.05);
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(var(--effect-blur));
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-index-head);
}

.dialog-content {
  background: var(--color-background);
  border-radius: var(--radius-lg);
  padding: 24px;
  min-width: 320px;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--color-surface-reverse);
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.dialog-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  color: var(--color-text-primary);
}

.close-btn {
  background: none;
  border: none;
  font-size: var(--font-size-xl);
  cursor: pointer;
  color: var(--color-text-primary);
  opacity: 0.6;
  transition: opacity 0.2s;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  opacity: 1;
}

.theme-options {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.theme-option {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: var(--color-surface-light);
  border: 2px solid transparent;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.2s;
  color: var(--color-text-primary);
}

.theme-option:hover {
  background: var(--color-surface);
  transform: translateY(-2px);
}

.theme-option.active {
  border-color: var(--color-brand);
  background: var(--color-surface);
}

.theme-option .icon {
  font-size: var(--font-size-2xl);
}

.theme-option .label {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
}

/* 다이얼로그 애니메이션 */
.dialog-enter-active,
.dialog-leave-active {
  transition: opacity 0.3s;
}

.dialog-enter-active .dialog-content,
.dialog-leave-active .dialog-content {
  transition: transform 0.3s, opacity 0.3s;
}

.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;
}

.dialog-enter-from .dialog-content,
.dialog-leave-to .dialog-content {
  transform: scale(0.9);
  opacity: 0;
}
</style>